import axios from 'axios';
import { API_BASE_URL } from '../../app_url';
/** Events emitted by BaseClient. Subscribe with client.on(event, listener). */
export const ClientEvent = Object.freeze({
  AUTH_REQUIRED: 'auth:required',
  UNAUTHENTICATED: 'auth:unauthenticated',
  TOKEN_REFRESHED: 'auth:token-refreshed',
  REFRESH_FAILED: 'auth:refresh-failed',
  REFRESH_TOKEN_EXPIRED: 'auth:refresh-token-expired',
  AUTH_CLEARED: 'auth:cleared',
  AUTH_REDIRECT: 'auth:redirect',
  REQUEST_STARTED: 'request:started',
  REQUEST_FINISHED: 'request:finished',
  REQUEST_FAILED: 'request:failed',
});

const AUTH_ERROR_CODES = new Set([
  'AUTH_REQUIRED',
  'UNAUTHENTICATED',
  'REFRESH_FAILED',
  'REFRESH_TOKEN_EXPIRED',
]);

export class RequestError extends Error {
  constructor(message, options = {}) {
    if (typeof options === 'number') options = { status: options };
    super(message, { cause: options.cause });
    this.name = 'RequestError';
    this.code = options.code || 'REQUEST_FAILED';
    this.status = options.status ?? options.response?.status ?? null;
    this.statusCode = this.status;
    this.response = options.response ?? null;
    this.details = options.details ?? null;
    this.request = options.request ?? null;
  }

  get isNetworkError() {
    return this.code === 'NETWORK_ERROR';
  }

  get isAuthError() {
    return AUTH_ERROR_CODES.has(this.code) || this.status === 401;
  }

  static fromResponse(response, request) {
    return new RequestError(
      response.message || response.statusText || `Request failed (${response.status})`,
      {
        code: response.status === 401 ? 'UNAUTHENTICATED' : 'HTTP_ERROR',
        status: response.status,
        response,
        details: response.errors,
        request: request ?? response.request,
      },
    );
  }
}

/** A small, stable facade over an Axios response. */
export class ResponseManager {
  constructor({ response, request }) {
    this.raw = response;
    this.data = response.data;
    this.request = request;
    this.status = response.status;
    this.statusText = response.statusText;
    this.ok = response.status >= 200 && response.status < 300;
    this.headers = response.headers;
    this.url = response.config?.url ?? request?.path ?? null;
  }

  get message() {
    return this.data?.message ?? this.data?.msg ?? null;
  }

  get errors() {
    return this.data?.errors ?? this.data?.error ?? null;
  }

  get pagination() {
    return this.data?.meta ?? this.data?.pagination ?? null;
  }

  header(name) {
    if (typeof this.headers?.get === 'function') return this.headers.get(name);
    const key = Object.keys(this.headers ?? {}).find(
      (header) => header.toLowerCase() === name.toLowerCase(),
    );
    return key ? this.headers[key] : undefined;
  }

  get(path, fallback = undefined) {
    if (!path) return this.data;
    const value = String(path).split('.').reduce(
      (current, key) => current?.[key],
      this.data,
    );
    return value === undefined ? fallback : value;
  }

  unwrap() {
    if (!this.ok) throw RequestError.fromResponse(this);
    return this.data;
  }
}

class EventEmitter {
  constructor(onListenerError) {
    this.listeners = new Map();
    this.onListenerError = onListenerError;
  }

  on(event, listener) {
    const listeners = this.listeners.get(event) ?? new Set();
    listeners.add(listener);
    this.listeners.set(event, listeners);
    return () => this.off(event, listener);
  }

  once(event, listener) {
    const unsubscribe = this.on(event, (payload) => {
      unsubscribe();
      listener(payload);
    });
    return unsubscribe;
  }

  off(event, listener) {
    this.listeners.get(event)?.delete(listener);
    return this;
  }

  emit(event, payload) {
    for (const listener of this.listeners.get(event) ?? []) {
      try {
        listener(payload);
      } catch (error) {
        this.onListenerError?.(error, event);
      }
    }
  }
}

class RequestBuilder {
  constructor(client, method, path, options = {}) {
    this.client = client;
    this.config = {
      method: method.toUpperCase(),
      path,
      query: options.query ?? options.params,
      body: options.body,
      headers: { ...options.headers },
      signal: options.signal,
      timeout: options.timeout,
      withCredentials: options.withCredentials ?? options.credentials,
      auth: options.auth ?? true,
      retryAuth: options.retryAuth ?? true,
      responseType: options.responseType ?? 'auto',
      context: options.context,
      onUploadProgress: options.onUploadProgress,
      onDownloadProgress: options.onDownloadProgress,
      axiosConfig: options.axiosConfig,
    };
    this.promise = null;
  }

  withAuth(options = {}) {
    this.config.auth = true;
    if (typeof options === 'boolean') this.config.retryAuth = options;
    else if (options.retry !== undefined) this.config.retryAuth = options.retry;
    return this;
  }

  withoutAuth() {
    this.config.auth = false;
    this.config.retryAuth = false;
    return this;
  }

  withHeaders(headers) {
    Object.assign(this.config.headers, headers);
    return this;
  }

  withQuery(query) {
    this.config.query = { ...this.config.query, ...query };
    return this;
  }

  withBody(body) {
    this.config.body = body;
    return this;
  }

  withTimeout(milliseconds) {
    this.config.timeout = milliseconds;
    return this;
  }

  withSignal(signal) {
    this.config.signal = signal;
    return this;
  }

  send() {
    if (!this.promise) {
      this.promise = this.client.send({
        ...this.config,
        headers: { ...this.config.headers },
        query: this.config.query ? { ...this.config.query } : undefined,
      });
    }
    return this.promise;
  }

  // Builders are awaitable: await service.get('/path') is equivalent to .send().
  then(onFulfilled, onRejected) {
    return this.send().then(onFulfilled, onRejected);
  }

  catch(onRejected) {
    return this.send().catch(onRejected);
  }

  finally(onFinally) {
    return this.send().finally(onFinally);
  }
}

class RequestScope {
  constructor(client, auth) {
    this.client = client;
    this.auth = auth;
  }

  request(method, path, options = {}) {
    return this.client.request(method, path, { ...options, auth: this.auth });
  }

  get(path, options) {
    return this.request('GET', path, options);
  }

  post(path, body, options = {}) {
    return this.request('POST', path, { ...options, body });
  }

  put(path, body, options = {}) {
    return this.request('PUT', path, { ...options, body });
  }

  patch(path, body, options = {}) {
    return this.request('PATCH', path, { ...options, body });
  }

  delete(path, options) {
    return this.request('DELETE', path, options);
  }
}

export class BaseClient {
  constructor(baseUrl = API_BASE_URL, options = {}) {
    if (baseUrl && typeof baseUrl === 'object') {
      options = baseUrl;
      baseUrl = options.baseUrl;
    }
    if (!baseUrl) throw new TypeError('BaseClient requires a baseUrl');

    this.baseUrl = String(baseUrl).replace(/\/+$/, '');
    this.defaultHeaders = { Accept: 'application/json', ...options.defaultHeaders };
    this.auth = {
      storageKey: options.auth?.storageKey ?? 'doctolikLoginInfo',
      scheme: options.auth?.scheme ?? 'Bearer',
      header: options.auth?.header ?? 'Authorization',
      getSession: options.auth?.getSession,
      getAccessToken: options.auth?.getAccessToken,
      getRefreshToken: options.auth?.getRefreshToken,
      setAccessToken: options.auth?.setAccessToken,
      clear: options.auth?.clear,
      refresh: options.auth?.refresh,
      extractToken: options.auth?.extractToken,
      extractRefreshToken: options.auth?.extractRefreshToken,
      applyToken: options.auth?.applyToken,
      isRefreshTokenExpired: options.auth?.isRefreshTokenExpired,
      loginPath: options.auth?.loginPath,
      redirect: options.auth?.redirect,
      redirectOnFailure: options.auth?.redirectOnFailure ?? true,
      clearOnFailure: options.auth?.clearOnFailure ?? true,
    };
    this.events = new EventEmitter(options.onListenerError);
    this.refreshPromise = null;

    this.axios = options.axiosInstance ?? axios.create({
      baseURL: this.baseUrl,
      headers: this.defaultHeaders,
      validateStatus: () => true,
    });
    this.installInterceptors();
  }

  installInterceptors() {
    this.axios.interceptors.request.use(async (config) => {
      if (config.__skipBaseClientAuth) return config;
      const token = await this.getAccessToken(config);
      if (!token) {
        const error = new RequestError('Authentication is required', {
          code: 'AUTH_REQUIRED',
          status: 401,
          request: config,
        });
        const payload = { request: config, error };
        this.events.emit(ClientEvent.AUTH_REQUIRED, payload);
        this.events.emit(ClientEvent.UNAUTHENTICATED, payload);
        await this.handleAuthenticationFailure(payload);
        throw error;
      }

      if (this.auth.applyToken) {
        await this.auth.applyToken(config, token);
      } else if (typeof config.headers?.set === 'function') {
        config.headers.set(this.auth.header, `${this.auth.scheme} ${token}`.trim());
      } else {
        config.headers = {
          ...config.headers,
          [this.auth.header]: `${this.auth.scheme} ${token}`.trim(),
        };
      }
      return config;
    });
  }

  on(event, listener) {
    return this.events.on(event, listener);
  }

  once(event, listener) {
    return this.events.once(event, listener);
  }

  off(event, listener) {
    return this.events.off(event, listener);
  }

  withoutAuth() {
    return new RequestScope(this, false);
  }

  withAuth() {
    return new RequestScope(this, true);
  }

  request(method, path, options) {
    return new RequestBuilder(this, method, path, options);
  }

  get(path, options) {
    return this.request('GET', path, options);
  }

  post(path, body, options = {}) {
    return this.request('POST', path, { ...options, body });
  }

  put(path, body, options = {}) {
    return this.request('PUT', path, { ...options, body });
  }

  patch(path, body, options = {}) {
    return this.request('PATCH', path, { ...options, body });
  }

  delete(path, options) {
    return this.request('DELETE', path, options);
  }

  /** Override to prefix or remap paths for a domain service. */
  resolvePath(path) {
    return path;
  }

  /** Override to return domain data instead of ResponseManager. */
  processResponse(response) {
    return response;
  }

  processError(error) {
    return error;
  }

  /** Extracts tokens from Doctolik and common OAuth response/session shapes. */
  extractToken(value) {
    if (this.auth.extractToken) return this.auth.extractToken(value);
    if (typeof value === 'string') return value;
    return value?.authorisation?.token
      ?? value?.authorization?.token
      ?? value?.accessToken
      ?? value?.access_token
      ?? value?.token
      ?? null;
  }

  extractRefreshToken(value) {
    if (this.auth.extractRefreshToken) return this.auth.extractRefreshToken(value);
    return value?.authorisation?.refresh_token
      ?? value?.authorization?.refresh_token
      ?? value?.refreshToken
      ?? value?.refresh_token
      ?? null;
  }

  getStoredSession() {
    if (this.auth.getSession) return this.auth.getSession();
    if (typeof localStorage === 'undefined') return null;
    try {
      const stored = localStorage.getItem(this.auth.storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  async getAccessToken(context) {
    if (this.auth.getAccessToken) return this.auth.getAccessToken(context);
    return this.extractToken(await this.getStoredSession());
  }

  async getRefreshToken(context) {
    if (this.auth.getRefreshToken) return this.auth.getRefreshToken(context);
    return this.extractRefreshToken(await this.getStoredSession());
  }

  async setAccessToken(token, refreshResult) {
    if (this.auth.setAccessToken) {
      return this.auth.setAccessToken(token, refreshResult);
    }
    if (typeof localStorage === 'undefined') return;
    const session = await this.getStoredSession();
    if (!session) return;
    session.authorisation = { ...session.authorisation, token };
    localStorage.setItem(this.auth.storageKey, JSON.stringify(session));
  }

  /** Override this method, or provide auth.refresh, when the API supports refresh. */
  async refreshToken(context) {
    const refreshToken = await this.getRefreshToken(context);
    return this.auth.refresh?.({ ...context, refreshToken, client: this });
  }

  canRefreshToken() {
    return Boolean(this.auth.refresh) || this.refreshToken !== BaseClient.prototype.refreshToken;
  }

  getLoginPath(session = this.getStoredSession()) {
    if (typeof this.auth.loginPath === 'function') return this.auth.loginPath(session);
    if (this.auth.loginPath) return this.auth.loginPath;
    const type = session?.users?.type?.toLowerCase();
    if (type === 'doctor') return '/doctor/login';
    if (type === 'nurse') return '/nurse/login';
    if (type === 'patient') return '/patient/login';
    // No stored session (e.g. the user just logged out): send them to the
    // site root rather than assuming a patient login.
    return session ? '/patient/login' : '/';
  }

  async clearAuthentication() {
    if (this.auth.clear) {
      await this.auth.clear();
    } else if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.auth.storageKey);
      localStorage.removeItem('isAciveDoctorAcc');
      if (typeof document !== 'undefined') {
        document.cookie = 'doctolikAuthToken=; Max-Age=0; path=/';
      }
    }
    this.events.emit(ClientEvent.AUTH_CLEARED, {});
  }

  redirectToLogin(path) {
    this.events.emit(ClientEvent.AUTH_REDIRECT, { path });
    if (this.auth.redirect) return this.auth.redirect(path);
    if (typeof window !== 'undefined') window.location.assign(path);
  }

  async handleAuthenticationFailure(context) {
    const session = await this.getStoredSession();
    const loginPath = await this.getLoginPath(session);
    if (this.auth.clearOnFailure) await this.clearAuthentication();
    if (this.auth.redirectOnFailure) await this.redirectToLogin(loginPath, context);
  }

  async send(config) {
    let request = config;
    try {
      request = await this.createAxiosConfig(config);
      this.events.emit(ClientEvent.REQUEST_STARTED, { request });
      let axiosResponse = await this.axios.request(request);
      let response = new ResponseManager({ response: axiosResponse, request });

      if (config.auth && response.status === 401) {
        this.events.emit(ClientEvent.UNAUTHENTICATED, { request, response });
        if (config.retryAuth && this.canRefreshToken()) {
          await this.refreshAccessToken({ request, response });
          request = await this.createAxiosConfig({ ...config, retryAuth: false });
          axiosResponse = await this.axios.request(request);
          response = new ResponseManager({ response: axiosResponse, request });
        }
        if (response.status === 401) {
          this.events.emit(ClientEvent.UNAUTHENTICATED, { request, response });
          await this.handleAuthenticationFailure({ request, response });
        }
      }

      if (!response.ok) throw RequestError.fromResponse(response, request);
      const result = await this.processResponse(response, request);
      this.events.emit(ClientEvent.REQUEST_FINISHED, { request, response, result });
      return result;
    } catch (cause) {
      const error = this.normalizeError(cause, request);
      const processedError = await this.processError(error, request);
      this.events.emit(ClientEvent.REQUEST_FAILED, { request, error: processedError });
      throw processedError;
    }
  }

  async createAxiosConfig(config) {
    const path = await this.resolvePath(config.path, config);
    const responseType = config.responseType === 'auto' ? undefined : config.responseType;
    return {
      ...config.axiosConfig,
      url: path,
      method: config.method,
      params: config.query,
      data: config.body,
      headers: { ...config.headers },
      signal: config.signal,
      timeout: config.timeout,
      withCredentials: config.withCredentials,
      responseType,
      onUploadProgress: config.onUploadProgress,
      onDownloadProgress: config.onDownloadProgress,
      // BaseClient handles HTTP statuses itself so refresh and ResponseManager
      // work consistently even with a caller-provided Axios instance.
      validateStatus: () => true,
      __skipBaseClientAuth: !config.auth,
      __baseClientContext: config.context,
    };
  }

  normalizeError(cause, request) {
    if (cause instanceof RequestError) return cause;
    if (axios.isCancel(cause) || cause?.code === 'ERR_CANCELED') {
      return new RequestError(cause?.message || 'Request aborted', {
        code: 'REQUEST_ABORTED',
        cause,
        request,
      });
    }
    if (axios.isAxiosError(cause)) {
      const status = cause.response?.status ?? null;
      return new RequestError(cause.message || 'API request failed', {
        code: status ? (status === 401 ? 'UNAUTHENTICATED' : 'HTTP_ERROR') : 'NETWORK_ERROR',
        status,
        response: cause.response ?? null,
        details: cause.response?.data?.errors ?? cause.response?.data,
        cause,
        request,
      });
    }
    return new RequestError(cause?.message || 'API request failed', {
      code: cause?.code === 'ERR_CANCELED' ? 'REQUEST_ABORTED' : 'REQUEST_FAILED',
      cause,
      request,
    });
  }

  async refreshAccessToken(context) {
    if (!this.refreshPromise) {
      this.refreshPromise = (async () => {
        try {
          const result = await this.refreshToken(context);
          const token = this.extractToken(result?.data ?? result);
          if (!token) {
            throw new RequestError('Token refresh returned no access token', {
              code: 'REFRESH_FAILED',
            });
          }
          await this.setAccessToken(token, result);
          this.events.emit(ClientEvent.TOKEN_REFRESHED, { token, result, ...context });
          return token;
        } catch (cause) {
          const expired = this.auth.isRefreshTokenExpired
            ? await this.auth.isRefreshTokenExpired(cause)
            : cause?.code === 'REFRESH_TOKEN_EXPIRED' || [401, 403].includes(
              cause?.status ?? cause?.response?.status,
            );
          const error = new RequestError(cause?.message || 'Unable to refresh authentication', {
            code: expired ? 'REFRESH_TOKEN_EXPIRED' : 'REFRESH_FAILED',
            status: cause?.status ?? cause?.response?.status,
            response: cause?.response,
            details: cause?.details,
            cause,
          });
          this.events.emit(ClientEvent.REFRESH_FAILED, { error, ...context });
          if (expired) {
            this.events.emit(ClientEvent.REFRESH_TOKEN_EXPIRED, { error, ...context });
          }
          await this.handleAuthenticationFailure({ error, ...context });
          throw error;
        } finally {
          this.refreshPromise = null;
        }
      })();
    }
    return this.refreshPromise;
  }
}

export const Client = BaseClient;
export default BaseClient;
